import { FlexColumn, FlexRow, Loading, StyledButton, useLoading } from '@gorila-shared-ui/components';
import { HeadingSmall } from 'baseui/typography';
import { useEffect } from 'react';
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from 'recoil';
import { FEEDBACK, FEEDBACK_PREFIXES } from '../../constants/app';
import { useFeedback } from '../../hooks/useFeedback';
import { useStyles } from '../../hooks/useStyles';
import { getFormDetail, updateForm } from '../../services/formsService';
import {
  editFormErrorState,
  editFormState,
  formDetailState,
  selectedFormIdState,
  updateFormFieldsState,
  updateFormsState,
} from '../../storage/formStates';
import { FormFields } from './FormFields';

export function FormDetail() {
  const { theme, css, classes } = useStyles();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const { loading: requesting, startLoading: startRequesting, stopLoading: stopRequesting } = useLoading();
  const [formDetail, setFormDetail] = useRecoilState(formDetailState);
  const [selectedFormId, setSelectedFormId] = useRecoilState(selectedFormIdState);
  const resetSelectedFormId = useResetRecoilState(selectedFormIdState);
  const [editForm, setEditForm] = useRecoilState(editFormState);
  const editFormHasError = useRecoilValue(editFormErrorState);
  const canSubmit = !editFormHasError;
  const setUpdateForms = useSetRecoilState(updateFormsState);
  const { showFailFeedback, showPositiveFeedback } = useFeedback();
  const [updateFormFields, setUpdateFormFields] = useRecoilState(updateFormFieldsState);

  useEffect(() => {
    return () => {
      setSelectedFormId(undefined);
      setFormDetail(undefined);
    };
  }, []);

  useEffect(() => {
    if (updateFormFields === 'update') {
      reloadFormDetail();
      setUpdateFormFields(undefined);
    }
  }, [updateFormFields]);

  useEffect(() => {
    if (!selectedFormId) {
      stopLoading();
      return;
    }
    startLoading();
    loadFormDetail();
    stopLoading();
  }, [selectedFormId]);

  const loadFormDetail = async () => {
    if (!selectedFormId) return;
    const { detail, error } = await getFormDetail(selectedFormId);
    if (!error && detail) {
      setFormDetail(detail);
      setEditForm((prev) => ({
        ...prev,
        active: detail.active,
        name: detail.name,
        tenants: detail.tenants.map((t) => t._id),
      }));
    } else {
      setSelectedFormId(undefined);
      showFailFeedback(error || 'No se pudo cargar el formulario');
    }
  };

  const reloadFormDetail = async () => {
    if (!selectedFormId) return;
    const { detail, error } = await getFormDetail(selectedFormId);
    if (!error && detail) {
      setFormDetail(detail);
    } else if (error) {
      showFailFeedback(error);
    }
  };

  if (!selectedFormId) return null;

  const onSaveForm = async () => {
    startRequesting();
    const { id: createdFormId, error } = await updateForm(selectedFormId, editForm);
    if (!error) {
      showPositiveFeedback(FEEDBACK.edited(FEEDBACK_PREFIXES.form, editForm.name));
      setUpdateForms(createdFormId ?? 'update');
    } else {
      showFailFeedback(error || FEEDBACK.failedEdition(FEEDBACK_PREFIXES.form, editForm?.name));
    }
    stopRequesting();
  };

  if (loading) {
    return <Loading />;
  }

  if (!formDetail) {
    return null;
  }

  return (
    <FlexColumn
      classNames={css({
        flex: 1,
        overflow: 'hidden',
        [theme.mediaQuery.extraLarge]: {
          maxWidth: '60%',
        },
      })}
    >
      <HeadingSmall margin={0}>{formDetail.name}</HeadingSmall>
      <FlexColumn classNames={css({ flex: 1, overflow: 'auto' })}>
        <FormFields />
      </FlexColumn>
      <FlexRow classNames={classes.centeredEnd}>
        <FlexRow gap={theme.sizing.scale300}>
          <StyledButton
            kind="tertiary"
            onClick={resetSelectedFormId}
          >
            Cancelar
          </StyledButton>
          <StyledButton
            onClick={onSaveForm}
            isLoading={requesting}
            disabled={!canSubmit}
          >
            Guardar
          </StyledButton>
        </FlexRow>
      </FlexRow>
    </FlexColumn>
  );
}
